import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Users, Database, Globe } from 'lucide-react';

const PrinciplesSection: React.FC = () => {
  const { t } = useTranslation('landing');

  const visionItems = [
    { icon: AlertTriangle, key: 'riskAlert', color: 'accent' },
    { icon: Users, key: 'communityCentered', color: 'ocean' },
    { icon: Database, key: 'aiPowered', color: 'sage' },
    { icon: Globe, key: 'collaboration', color: 'sand' }
  ];

  return (
    <section className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
            {t('ourVision.title')}
          </h2>
          <p className="spectrum-body text-xl spectrum-text-muted max-w-5xl mx-auto leading-relaxed mb-12">
            {t('ourVision.subtitle')}
          </p>
        </motion.div>

        {/* Vision Statement Paragraphs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto mb-16 space-y-8"
        >
          <div className="bg-white border-2 border-gray-300 p-8 lg:p-12">
            <p className="spectrum-body text-lg spectrum-text-dark leading-relaxed mb-6">
              By enabling earlier <strong>risk detection (RISK)</strong>, timely <strong>early warning (ALERT)</strong>, and <strong>situation awareness (SITREP)</strong>, CLEAR will make emergency response better informed, faster, and where possible, anticipatory.
            </p>
            <p className="spectrum-body text-lg spectrum-text-dark leading-relaxed mb-6">
              CLEAR will transform how we understand and respond to crises by centering community voices at each step of the response, capturing their perspectives wherever feasible, and empowering (local) emergency responders with timely access to the right information, and solutions at critical moments.
            </p>
            <p className="spectrum-body text-lg spectrum-text-dark leading-relaxed mb-6">
              Leveraging modern data infrastructure, predictive analytics, and AI, CLEAR will extract insights, synthesize information, and communicate effectively, turning raw data into actionable intelligence enabling timely action and further bringing focus on the much needed service delivery.
            </p>
            <p className="spectrum-body text-lg spectrum-text-dark leading-relaxed">
              CLEAR represents an opportunity to consolidate efforts, break organizational silos, and bring diverse stakeholders from different sectors together around a shared goal to bring AI to the service of humanity.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {visionItems.map((item, index) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="spectrum-card group"
            >
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-white border border-gray-300 flex items-center justify-center hover:scale-110 transition-transform">
                    <item.icon className={`w-8 h-8 ${
                      item.color === 'accent' && 'text-orange-500'
                    } ${
                      item.color === 'ocean' && 'text-blue-500'
                    } ${
                      item.color === 'sage' && 'text-green-500'
                    } ${
                      item.color === 'sand' && 'text-yellow-500'
                    }`} />
                  </div>
                </div>
                <h3 className="spectrum-headline text-lg spectrum-text-dark font-bold">
                  {t(`ourVision.items.${item.key}.title`)}
                </h3>
                <p className="spectrum-body text-sm spectrum-text-muted leading-relaxed">
                  {t(`ourVision.items.${item.key}.description`)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Responsible AI Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto bg-white border-2 border-orange-500 p-8 lg:p-12"
        >
          <h3 className="spectrum-headline text-2xl spectrum-text-dark font-bold mb-6 text-center">
            {t('ourVision.responsible.title')}
          </h3>
          <div className="space-y-4 text-left">
            <p className="spectrum-body text-base spectrum-text-dark leading-relaxed">
              Humanitarians operate in challenging contexts often characterised by limited data availability and connectivity. While holding immense potential in supporting humanitarian action, AI and emerging technologies are tools, not silver bullets.
            </p>
            <p className="spectrum-body text-base spectrum-text-dark leading-relaxed">
              We remain committed to responsible AI use, maintaining alignment with humanitarian principles and ensuring continuous human evaluation and oversight.
            </p>
            <p className="spectrum-body text-base spectrum-text-dark leading-relaxed">
              AI cannot replace human judgment, local knowledge, or the fundamental work of humanitarian responders and the sector should remain humble about the technology's limitations and committed to being transparent about where it can and cannot help.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PrinciplesSection;